import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { PubNubProvider } from "pubnub-react"
import AutoSizer from "react-virtualized-auto-sizer"
import { Chat } from "@pubnub/react-chat-components"
import { useLocation, useNavigate } from "react-router-dom"
import { AiOutlineDown } from "react-icons/ai"
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io"
import moment from "moment"
import Dropzone from "react-dropzone"
import Audio from "../Audio"
import { Spinner } from "../Loading"
import systemChat from "../../assets/icons/system-chat-toggle.svg"
import { Button } from "../Button"
import {
  BsFiletypeCsv,
  BsFiletypeDocx,
  BsFiletypePdf,
  BsFiletypePptx,
  BsFiletypeXlsx,
} from "react-icons/bs"
import { VariableSizeList } from "react-window"
import { useMediaQuery } from "react-responsive"
import { ArrowSendIcon } from "../ImageComponents"
import { Input } from "../Form"
import { ActionsMenu } from "../ActionsMenu"
import { getHighlightedText } from "../HighlightedText"
import { SessionChatBadge } from "../Badge"
import { ButtonClose } from "../ChatCloseButton"
import { useChatContext } from "../../core/context/chatContext"
import { cleanMessageTemplate } from "../../container/NewChatContainer"
import { CellMeasurer, CellMeasurerCache, List } from "react-virtualized"
import "./styles.scss"
import { PickerList } from "../PickerDropdow"
import { InfoTooltip } from "../Tooltip"
import useWindowDimensions from "../Modal"
import { toast } from "react-toastify"
import { IoIosAdd } from "react-icons/io"
import { PiTrashFill } from "react-icons/pi"

import { isMobile } from "react-device-detect"
import ModalHeaderBack from "../ModalHeaderBack"
import Overlay from "../Overlay"

export const ListViewChat = ({ data, onSelectChat, selectedChat, loading, className, read, onEditUser }) => {
  const medium = useMediaQuery({ query: "(max-width:1366px)" })
  const rowHeights = useRef({})
  const listRef = useRef(null)
  
  // Calculate row height based on whether the item has tags
  const getItemSize = useCallback((index) => {
    const row = data[index]
    const hasTags = row?.tags && row.tags.length > 0
    // Base height + additional height for tags
    return hasTags ? (medium ? 110 : 120) : (medium ? 90 : 100)
  }, [data, medium])
  
  // Reset cache when data changes
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0)
    }
  }, [data])
  
  const Row = ({ index, style }) => {
    const row = data[index]

    const handleEditClick = (e) => {
      e.stopPropagation() // Prevent row selection when clicking edit button
      if (onEditUser) {
        onEditUser(row)
      }
    }

    return (
      <>
        {row && (
          <div style={style} className={`row-style ${row.id === selectedChat.id ? "active" : ""}`}>
            <li
              className={`list-view-chat__item ${row.id === selectedChat.id ? "active" : ""} ${className ? ` ${className}` : ""
                }`}
              onClick={() => onSelectChat(row)}>
              <div className="item-box-info">
                <div className="avatar-image">
                  <img
                    className="img-avatar"
                    src={
                      row?.image
                        ? row.image
                        : `https://ui-avatars.com/api/?background=random&name=${row.name}`
                    }
                  />
                  {!read && <SessionChatBadge sessionTime={row?.lastMessageDate} />}
                </div>
                <div className="user-details">
                  {row.tags && row.tags.length > 0 && (
                    <div className="tags-container">
                      {row.tags.slice(0, medium ? 2 : 3).map((tag, index) => (
                        <span 
                          key={tag.id} 
                          className="user-tag"
                          style={{
                            '--tag-bg-color': tag.color,
                            '--tag-text-color': tag.text_color
                          }}
                          title={tag.name.length > 15 ? tag.name : undefined}
                        >
                          <span className="user-tag__text">{tag.name}</span>
                        </span>
                      ))}
                      {row.tags.length > (medium ? 2 : 3) && (
                        <span 
                          className="more-tags" 
                          title={row.tags.slice(medium ? 2 : 3).map(tag => tag.name).join(', ')}
                        >
                          +{row.tags.length - (medium ? 2 : 3)}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="user-details__header">
                    <span className="user-details__header-project" title={row.projectNames}>
                      {row.projectNames?.join(" | ")}
                    </span>
                    <span
                      className="user-details__header-last-message-date"
                      title={row.projectNames}>
                      {moment(row?.date).format("DD/MM/YYYY")}
                    </span>
                  </div>
                  <div className="user-details__info">
                    <span className="user-details__info-name" title={row.name}>
                      {row.name}
                    </span>
                    <span className="user-details__info-last-message" title={row.message}>
                      <span>{row.message}</span>
                    </span>
                    {!row.readByUser ? <span className="count-unread-message"></span> : <></>}
                  </div>
                </div>
                
                {/* Edit button - appears on hover */}
                {onEditUser && (
                  <div className="edit-user-button" onClick={handleEditClick}>
                    <i className="fi fi-rr-pencil" title="Editar usuário"></i>
                  </div>
                )}
              </div>
            </li>
          </div>
        )}
      </>
    )
  }

  return (
    <div className={`list-view-chat${className ? ` ${className}` : ""}`}>
      <AutoSizer className="div-channel-scroller">
        {({ height, width }) => (
          <>
            {!selectedChat && loading ? (
              <div className="loading-content" style={{ height, width }}>
                <Spinner color="primary" />
              </div>
            ) : data.length > 0 ? (
              <>
                <VariableSizeList
                  ref={listRef}
                  height={read ? height - 120 : height}
                  itemCount={data.length}
                  itemSize={getItemSize}
                  width={width}
                  itemData={
                    (data &&
                      data
                        ?.filter((row) => row.date)
                        .sort((a, b) => a?.date?.getTime() > b?.date?.getTime())) ||
                    []
                  }
                  innerElementType="ul"
                  className="list-view-chat-scroll">
                  {Row}
                </VariableSizeList>
              </>
            ) : (
              <div className="channels-empty" style={{ height, width }}>
                <h3>Não há chats</h3>
              </div>
            )}
          </>
        )}
      </AutoSizer>
    </div>
  )
}

export const ChannelsList = ({ selectedChat, onSelectChat, channels, loading, read, onEditUser }) => {
  return (
    <aside className="channels-chat">
      <ListViewChat
        data={channels}
        onSelectChat={onSelectChat}
        selectedChat={selectedChat}
        loading={loading}
        read={read}
        onEditUser={onEditUser}
      />
    </aside>
  )
}

export const ChatMessageHeader = ({
  chat,
  menuList,
  openHistory,
  setOpenHistory,
  openSearch,
  setOpenSearch,
  onChange,
  goToFiltered,
  showOnlyChat,
  handleToggleSystemChat,
  isSystemChat,
  withoutProfile,
  backToList = null,
  handleCloseModal,
  formQuestionMessage,
  handleCloseChat,
  handleNavigateToEvidence = () => { },
  outSideContainer,
}) => {
  const location = useLocation()
  const isHistoryOrSearchOpen = openHistory || openSearch

  const toggleOpenState = () => {
    if (openHistory) {
      setOpenHistory(false)
    } else {
      setOpenSearch(false)
    }
  }

  const closeChat = () => {
    if (backToList && typeof backToList === 'function') {
      backToList()
    } else {
      handleCloseChat()
      handleNavigateToEvidence()
    }
  }

  return (
    <header
      className={`message-header ${isHistoryOrSearchOpen ? "open-history" : ""} ${isSystemChat ? "system-chat" : ""
        } ${showOnlyChat ? "only-chat" : "chat-and-messages"}`}>
      {!isHistoryOrSearchOpen ? (
        <>
          <div className="new-chat-container">
            <div className="new-chat-container-title-buttons-left">
              <div className="new-chat-container-buttons">
                {(handleCloseModal && showOnlyChat && !isSystemChat) ||
                  (isMobile && !isSystemChat) ||
                  (!isSystemChat && backToList) ? (
                  <button
                    style={{
                      height: 40,
                      width: 40,
                      backgroundColor: "#f2f2f2",
                      borderRadius: "100%",
                      border: "none",
                    }}
                    className="button-back"
                    onClick={
                      handleCloseModal && showOnlyChat
                        ? handleCloseModal
                        : isMobile
                          ? closeChat
                          : backToList
                    }>
                    <i className="fi fi-sr-arrow-small-left" />
                  </button>
                ) : null}
              </div>

              <div className="new-chat-container-title">
                <span style={showOnlyChat || isSystemChat ? { fontWeight: "bold" } : {}}>
                  {isSystemChat ? "Assistente de impacto" : "Conversa"}
                </span>
              </div>
            </div>

            <div className="new-chat-container-title-buttons-right">
              <div className="new-chat-container-buttons">
                <div className="chat-header-actions">
                  {isSystemChat ? (
                    <ButtonClose type="secondary" handleClick={handleToggleSystemChat} />
                  ) : (
                    <>
                      <SessionChatBadge sessionTime={chat?.lastMessageDate} size="lg" hasIcon isStatic />
                      {(showOnlyChat || isMobile) && (
                        <div className="system-chat-toggle" onClick={handleToggleSystemChat}>
                          <img src={systemChat} alt="system chat" />
                        </div>
                      )}
                      {!outSideContainer && <ActionsMenu options={menuList} />}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

        </>
      ) : (
        <>
          {!showOnlyChat && <span>{openHistory ? "Histórico" : "Pesquisar"}</span>}
          <Input
            placeholder="Pesquise"
            hasIcon={<i className="fi fi-rr-search"></i>}
            onChange={onChange}
            hasFocus={false}
          />
          <div className="search-arrow__container">
            <button className="button-input-open" onClick={() => goToFiltered("up")}>
              <IoIosArrowUp color="#004001" />
            </button>
            <button className="button-input-open" onClick={() => goToFiltered("down")}>
              <IoIosArrowDown color="#004001" />
            </button>
          </div>
          <button className="button-input-open" onClick={toggleOpenState}>
            <i className="fi fi-sr-cross-circle"></i>
          </button>
        </>
      )}
    </header>
  )
}

export const SendInput = ({
  placeholder = "Digite uma mensagem",
  onSend,
  onKeyPress,
  onChangeMessage,
  valueMessage,
  onHeightChange,
  handleOpenText,
  openText,
  handleOpenVoice,
  openVoice,
  isSystemChat,
  chat,
  onSendFile,
  onChangeVoic,
  valueVoice,
  withoutProfile,
  outSideContainer,
  messages,
}) => {

  const [hasAudio, setHasAudio] = useState("")
  const [isDisplay, setDisplayButton] = useState("block")
  const [isLocked, setIsLocked] = useState(false)

  const textareaRef = useRef(null)
  const inputShapeRef = useRef(null)
  const containerRef = useRef(null)

  const lastMessage = messages[messages.length - 1]

  const isUserExistsInreadByUsers =
    lastMessage?.readByUsers?.length > 0 && lastMessage.readByUsers[0] === lastMessage.user

  const isSmallScreen = useMediaQuery({ query: "(max-height: 768px)" })

  const handleClickOutside = useCallback(
    (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleOpenText(false)
        handleOpenVoice(false)
        setHasAudio("")
        setDisplayButton("block")
      }
    },
    [handleOpenText, handleOpenVoice],
  )

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    if (textareaRef.current) {
      const newHeight =
        valueMessage.length === 0
          ? isSmallScreen
            ? 48
            : 56
          : Math.min(textareaRef.current.scrollHeight, 200)

      textareaRef.current.style.height = `${newHeight}px`
      onHeightChange(newHeight)
    }
  }, [valueMessage, isSmallScreen, onHeightChange])

  useEffect(() => {
    if (hasAudio) {
      onHeightChange(120)
    } else {
      onHeightChange(isSmallScreen ? 48 : 56)
    }
    setDisplayButton("block")
  }, [hasAudio, onHeightChange, isSmallScreen])

  useEffect(() => {
    setDisplayButton(openText ? "none" : "block")
  }, [openText])

  useEffect(() => {
    if (openText) {
      textareaRef?.current?.focus()
    }
  }, [openText])

  const handleSend = useCallback(
    (e) => {
      setHasAudio("")
      onSend(e)
      handleOpenText(false)
      handleOpenVoice(false)
    },
    [onSend, handleOpenText, handleOpenVoice],
  )

  const renderTextArea = () => (
    <>
      <textarea
        ref={textareaRef}
        placeholder={placeholder}
        className="input-message__area-field after-change"
        onChange={onChangeMessage}
        value={valueMessage}
        style={{ height: isSmallScreen ? 48 : 56 }}
        onKeyDown={onKeyPress}
      />
      <button className="send-message-button text" onClick={handleSend} title="Enviar mensagem">
        <ArrowSendIcon />
      </button>
    </>
  )

  const renderVoiceArea = () => (
    <div ref={textareaRef} className="input-message__area-voice-field after-change">
      <div className="audio-player">
        {hasAudio && <audio id="audio" controls src={hasAudio} className="audio-component"></audio>}
      </div>
    </div>
  )

  const changes = openText || openVoice ? "after-change" : "before-change"

  return (
    <>
      <div className="input-message-wrapper">
        <div
          ref={containerRef}
          className={`input-message-container ${changes} ${withoutProfile && !outSideContainer ? "only-messages" : outSideContainer ? "only-messages-outside-container" : "chat-and-messages"
            }`}>
          {!withoutProfile && !isMobile && (
            <div className="avatar user-chat-avatar">
              <div className="avatar-img">
                <img
                  className="img-avatar"
                  src={
                    chat.image
                      ? chat.image
                      : `https://ui-avatars.com/api/?background=random&name=${chat?.name}`
                  }
                />
              </div>
            </div>
          )}
          <div
            className={`input-message ${hasAudio ? "audio" : "text"} ${changes} ${isSystemChat ? "white-variation" : ""
              }`}
            style={{ borderRadius: "0px !important" }}>
            {!openText && !openVoice && !hasAudio && (
              <div>
                <Dropzone
                  onDrop={(file) => onSendFile(file)}
                  maxSize={16711876}
                  noDrag={true}
                  maxFiles={1}
                  multiple={false}
                  onDropRejected={(array) => {
                    if (array && array.length > 0) {
                      const fileLarge =
                        array.filter((item) =>
                          item?.errors?.find((row) => row?.code === "file-too-large"),
                        ).length > 0
                      if (fileLarge) {
                        toast.error("Permitido somente arquivos de até 16mb")
                      }
                    }
                  }}>
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      className="input-message__add"
                      role="button"
                      title="Fazer upload de xdd">
                      <input {...getInputProps()} />
                      <div>
                        <IoIosAdd />
                        {/* <i className="fi fi-sr-file-upload"></i> */}
                      </div>
                    </div>
                  )}
                </Dropzone>
              </div>
            )}
            {/* <div style={{ fontSize: "30px", display: "flex", color: "white", alignItems: "center" }}>
            <IoIosAdd />
            </div> */}
            <div
              className={`input-message__area ${!openText && !hasAudio ? "before-change" : "after-change"
                } ${openText && !hasAudio ? "text" : ""} ${hasAudio ? "audio" : ""}`}
              role="button"
              ref={inputShapeRef}
              onClick={() => !openText && !hasAudio && handleOpenText(true)}>
              {openText && !hasAudio && renderTextArea()}
              {!openText && !hasAudio && (
                <div className="input-message__area-field before-change">{placeholder}</div>
              )}
              {hasAudio && renderVoiceArea()}
            </div>
            {!openVoice && (
              <Audio
                onChange={(value, file) => onChangeVoic(value, file)}
                value={valueVoice && valueVoice.value}
                setHasAudio={setHasAudio}
                hasAudio={hasAudio}
                removeAudio={chat}
                handleSend={handleSend}
                isDisplay={isDisplay}
                setDisplayButton={setDisplayButton}
                isUserExistsInreadByUsers={isUserExistsInreadByUsers}
                isLocked={isLocked}
                setIsLocked={setIsLocked}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const ItemMessage = ({
  chat,
  loggedUser,
  item,
  setOpenPicker,
  openPicker,
  onDeleteMessage,
  valueSearch,
  isSystemChat,
  showActions,
  location,
  formQuestionMessage,
  handleTestimonial = () => { },
  handleRemoveTestimonial = () => { },
  handleNavigateToEvidence
}) => {
  const {
    enableLinkComponent,
    addNewPinnedMessage,
    removePinnedMessage,
    pinnedMessages,
    pinnedMessagesQuestions,
    handleSaveOnlyOneMessage,
    evidenceId,
    favoritedMessages,
    setFavoritedMessages,
    updatedMessages,
    setUpdatedMessages
  } = useChatContext()

  const notLoggedUser =
    chat?.id === item.user || item.user === "system_chat_id" ? "no-logged-user" : "logged-user"

  const dateMessage = useMemo(() => {
    return new Date(item?.date?.seconds * 1000)
  }, [item?.date])

  const handleClickFavorite = () => {
    const isAlreadyFavorited =
      item?.favorited?.status === "added" ||
      updatedMessages[item?.id]?.favorited?.status === "added" ||
      favoritedMessages.some(fav => fav.id === item.id);

    if (isAlreadyFavorited) {

      setUpdatedMessages(prev => ({
        ...prev,
        [item.id]: {
          ...item,
          favorited: { status: "removed" }
        }
      }));

      setFavoritedMessages(prev =>
        prev.filter(fav => fav.id !== item.id)
      );

      removePinnedMessage(item?.id);
      handleRemoveTestimonial(chat, item);
    } else {

      setUpdatedMessages(prev => ({
        ...prev,
        [item.id]: {
          ...item,
          favorited: { status: "added" }
        }
      }));

      setFavoritedMessages(prev => [...prev, { ...item, favorited: { status: "added" } }]);

      addNewPinnedMessage({ ...item, loggedUser });
      handleSaveOnlyOneMessage(true);
      handleTestimonial(chat);
    }
  };

  const hasPinnedMessage = useMemo(() => {
    const pinnedFromQuestions = pinnedMessagesQuestions?.[`${evidenceId}-messages`];
    const source = Array.isArray(pinnedFromQuestions) && pinnedFromQuestions.length > 0
      ? pinnedFromQuestions
      : pinnedMessages

    return Array.isArray(source) ? source.find((row) => row?.id === item?.id) : undefined;
  }, [pinnedMessages, pinnedMessagesQuestions, item?.id, evidenceId])

  const imageSrc = useMemo(() => {
    return (chat.id === item.user || item.user === "system_chat_id") && isSystemChat
      ? systemChat
      : item.image
        ? item.image
        : `https://ui-avatars.com/api/?background=random&name=${item.nameSender}`
  }, [chat?.id, item?.user, item?.image, item?.nameSender, isSystemChat])

  const shouldRenderTooltip = !isSystemChat && enableLinkComponent && !item?.ErrorMessage;

  const notShowPicker =
    location?.pathname?.includes("/farmer/profile") &&
    location?.search?.includes("?context=") &&
    !formQuestionMessage;

  const updated = updatedMessages ? updatedMessages[item?.id] : {};
  const status = updated?.favorited?.status ?? item?.favorited?.status;

  const isFavoritedAdded = status === "added";

  return (
    <>
      <div className={`messages-users ${notLoggedUser}`} key={item?.id}>
        <div className={`message-user-box ${notLoggedUser}`}>
          {item.deleted ? (
            <span className="deleted-message">Esta mensagem foi apagada</span>
          ) : (
            <React.Fragment>
              <div className={`message-user ${notLoggedUser}`} id={item?.id || ""}>
                <div className={`message-user-details ${notLoggedUser}`}>
                  <div className={`message-user-details-avatar ${notLoggedUser}`}>
                    <img className="img-avatar" src={imageSrc} />
                    <span className={`text-user ${notLoggedUser}`} title={`${item.nameSender}`}>
                      {item.nameSender}
                    </span>
                  </div>
                  <span className="message-date">{`${moment(dateMessage).format("DD/MM")} às ${moment(
                    dateMessage,
                  ).format("LT")}`}</span>
                </div>
                {loggedUser.id === item.user ? (
                  <AiOutlineDown
                    className={`icon-down ${notLoggedUser}`}
                    style={{ marginLeft: 15 }}
                    onClick={() => setOpenPicker(item)}
                  />
                ) : (
                  <div />
                )}
              </div>
              <div
                className={`div-collumn-chat message-bubble ${notLoggedUser} ${isSystemChat ? "white-variation" : ""
                  }`}>
                {textVerification(valueSearch, item, loggedUser, chat?.id)}
                {!isMobile && shouldRenderTooltip && (
                  <>
                    <InfoTooltip
                        info={`${!hasPinnedMessage ? `Selecionar Mensagem` : `Desselecionar Mensagem`}`}
                        trigger="hover"
                      >
                        <div
                          className="add_message"
                          onClick={() => {
                            if (hasPinnedMessage) {
                              removePinnedMessage(item?.id, evidenceId);
                            } else {
                              addNewPinnedMessage({ ...item, loggedUser }, evidenceId);
                            }
                          }}
                        >
                          <div
                            className={`message_chat ${!hasPinnedMessage
                              ? "add_icon"
                              : "remove_icon"
                              }`}
                          />
                        </div>
                      </InfoTooltip>
                  </>
                )}
                {isMobile && shouldRenderTooltip && (
                  <div className="add_message">
                    <div className="message-text-icons">
                      <div
                        onTouchStart={handleClickFavorite}
                        className={`message_chat ${isFavoritedAdded ? "remove_favorited" : "star_icon"}`}
                      />
                      <div
                        onClick={() =>
                          !hasPinnedMessage
                            ? addNewPinnedMessage({ ...item, loggedUser }, evidenceId)
                            : removePinnedMessage(item?.id, evidenceId)
                        }
                        className={`message_chat ${!hasPinnedMessage ? "add_icon" : "remove_icon"
                          }`}
                      />
                    </div>
                  </div>
                )}
                {notLoggedUser === "logged-user" && item?.MessageStatus === "read" && (
                  <InfoTooltip
                    info="Mensagem visualizada"
                    trigger="hover"
                    children={
                      <div className="remove_message" onClick={() => onDeleteMessage(item)}>
                        <div className="message_chat remove_icon">
                          <i class="fi fi-sr-trash-restore-alt"></i>
                        </div>
                      </div>
                    }
                  />
                )}
                {!isSystemChat &&
                  <InfoTooltip
                    info="Excluir Mensagem"
                    trigger="hover"
                    children={
                      <div className="remove_message" onClick={() => onDeleteMessage(item)}>
                        <div className="message_chat remove_icon">
                          <i class="fi fi-sr-trash-restore-alt"></i>
                        </div>
                      </div>
                    }
                  />}
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    </>
  )
}

export const SendMessage = forwardRef(
  (
    {
      pubnub,
      currentChannel,
      theme,
      chat,
      selectedChat,
      messages,
      loggedUser,
      loading,
      setOpenPicker,
      openPicker,
      onDeleteMessage,
      newChat,
      createdAt,
      valueSearch,
      isSystemChat,
      showActions,
      onChangeVoic,
      valueVoice,
      onChangeMessage,
      valueMessage,
      onSend,
      onSendFile,
      onKeyPress,
      withoutProfile,
      outSideContainer,
      handleOpenText,
      openText,
      handleOpenVoice,
      openVoice,
      resendMessage,
      openHistory,
      openSearch,
      formQuestionMessage,
      handleTestimonial,
      handleRemoveTestimonial,
      handleNavigateToEvidence,
      reload,
      isLoadingMessages
    },
    ref,
  ) => {
    const [textareaHeight, setTextareaHeight] = useState(64)
    const [inititalData, setInitialData] = useState([])
    const { height, width } = useWindowDimensions()
    const location = useLocation()

    const listRef = useRef(null)
    const rowRef = useRef(null)
    const previousMessagesLength = useRef(messages?.length)

    useEffect(() => {
      if (chat || isSystemChat || openHistory || openSearch) {
        setInitialData(messages)
      }
    }, [chat, isSystemChat, openHistory, openSearch])

    useImperativeHandle(ref, () => ({
      getInternalRef: () => {
        return listRef.current
      },
    }))

    const cache = useMemo(() => {
      return new CellMeasurerCache({
        defaultHeight: 600, // Altura padrão
        fixedHeight: false, // Permite altura dinâmica
      })
    }, [height, width, inititalData])

    const handleTextareaHeightChange = useCallback((newHeight) => {
      setTextareaHeight(newHeight)
    }, [])

    const scrollToBottom = () => {
      if (listRef.current) {
        listRef.current.scrollToRow(messages?.length - 1)
      }
    }

    useEffect(() => {
      if (messages?.length > previousMessagesLength.current) {
        scrollToBottom()
      }

      previousMessagesLength.current = messages?.length
    }, [messages])

    const Row = useCallback(
      ({ index, key, parent, style }) => {
        const currMessage = messages[index]

        const errors = currMessage?.ErrorMessage && getErrors(currMessage?.ErrorMessage)

        return (
          <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
            <div style={{ ...style }}>
              <div
                ref={rowRef}
                className={`list-messages ${errors && !currMessage?.deleted ? "list-messages-error" : ""
                  }`}>
                {!currMessage.deleted ? (
                  <>
                    {dateMessage(currMessage, createdAt) && (
                      <div className="date-message">
                        <span className="text-date-message">
                          {dateMessage(currMessage, createdAt)}
                        </span>
                      </div>
                    )}
                    <div className={`list-messages-row ${errors ? "list-messages-error-row" : ""}`}>
                      {errors ? (
                        <div className="error-message">
                          <div className="error-title-container">
                            <div className="error-title">
                              <i className="fi fi-sr-exclamation"></i>
                              <span>Erro ao enviar mensagem</span>
                            </div>
                            {/* <i className="fi fi-sr-trash"></i> */}
                          </div>
                          <span className="message">{errors}</span>
                        </div>
                      ) : (
                        ""
                      )}
                      <div
                        className={`message-container ${errors ? "message-container-error" : ""}`}
                        key={index}>
                        <ItemMessage
                          chat={chat}
                          valueSearch={valueSearch}
                          loggedUser={loggedUser}
                          item={currMessage}
                          setOpenPicker={!!errors ? false : setOpenPicker}
                          openPicker={!!errors ? false : openPicker}
                          onDeleteMessage={onDeleteMessage}
                          isSystemChat={isSystemChat}
                          showActions={showActions}
                          location={location}
                          withoutProfile={withoutProfile}
                          formQuestionMessage={formQuestionMessage}
                          handleTestimonial={handleTestimonial}
                          handleRemoveTestimonial={handleRemoveTestimonial}
                          handleNavigateToEvidence={handleNavigateToEvidence}
                        />
                      </div>
                      {errors ? (
                        <div className="resend-message">
                          <button onClick={() => resendMessage({ ...currMessage, chat })}>
                            Reenviar Mensagem
                          </button>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </>
                ) : (
                  <div className={`message-container`} key={index}>
                    <ItemMessage
                      chat={chat}
                      valueSearch={valueSearch}
                      loggedUser={loggedUser}
                      item={currMessage}
                      setOpenPicker={!!errors ? false : setOpenPicker}
                      openPicker={!!errors ? false : openPicker}
                      onDeleteMessage={onDeleteMessage}
                      isSystemChat={isSystemChat}
                      showActions={showActions}
                      location={location}
                      withoutProfile={withoutProfile}
                      formQuestionMessage={formQuestionMessage}
                      handleTestimonial={handleTestimonial}
                      handleRemoveTestimonial={handleRemoveTestimonial}
                      handleNavigateToEvidence={handleNavigateToEvidence}
                    />
                  </div>
                )}
              </div>
            </div>
          </CellMeasurer>
        )
      },
      [messages, cache, reload],
    )

    const calculateMarginBottom = (height) => {
      const baseHeight = 10
      const adjustment = Math.min(Math.max(height, 20), 20)
      return baseHeight + adjustment
    }

    return (
      <>
        <PubNubProvider client={pubnub}>
          <Chat {...{ currentChannel, theme }}>
            {loading ? (
              <div className="class-spinner">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                {!selectedChat ? (
                  verificationChat(selectedChat, chat.hasChat, newChat)
                ) : (
                  <div
                    className={`message-list-container ${withoutProfile && !outSideContainer ? "only-messages" : outSideContainer ? "only-messages-outside-container" : "chat-and-messages"
                      }`}
                    style={{ display: "flex", flexDirection: "column" }}>
                    <div
                      style={{
                        height: "100%",
                        marginBottom: isMobile
                          ? "0px"
                          : `${calculateMarginBottom(textareaHeight)}px`,
                      }}>
                      <AutoSizer>
                        {({ height, width }) => (
                          <List
                            ref={listRef}
                            deferredMeasurementCache={cache}
                            height={height - textareaHeight}
                            rowCount={messages?.length}
                            rowHeight={cache.rowHeight}
                            rowRenderer={Row}
                            scrollToIndex={messages?.length - 1}
                            width={width}
                          />
                        )}
                      </AutoSizer>
                    </div>
                    <div className="send-input-container">
                      <SendInput
                        {...{
                          onHeightChange: handleTextareaHeightChange,
                          onChangeVoic,
                          valueVoice,
                          onChangeMessage,
                          valueMessage,
                          onSend,
                          onSendFile,
                          onKeyPress,
                          handleOpenText,
                          openText,
                          handleOpenVoice,
                          openVoice,
                          chat,
                          withoutProfile,
                          outSideContainer,
                          messages,
                        }}
                      />
                    </div>
                  </div>
                )}
                <div></div>
              </>
            )}
          </Chat>
        </PubNubProvider>
      </>
    )
  },
)

const verificationChat = (selectedChat, hasChat, newChat) => {
  return (
    <div className="not-chat">
      {!selectedChat ? (
        <span>Escolha uma conversa para enviar mensagem</span>
      ) : !hasChat ? (
        <Button label={"Nova Conversa"} handleClick={newChat} />
      ) : (
        <div />
      )}
    </div>
  )
}

const dateMessage = (data, createdAt) => {
  const formatedDate = createdAt.filter((row) => {
    return data.date === row.date
  })
  return formatedDate[0] && formatedDate[0].createdAt
}

const MessageDocComponent = ({ item, loggedUser }) => {
  const handleAttachmentClick = () => {
    if (item.attachment) {
      window.open(item.attachment.remotePath)
    }
  }

  const getFileIcon = () => {
    if (item.attachment) {
      if (item.attachment.contentType.includes("pdf")) {
        return <BsFiletypePdf size={20} />
      } else if (item.attachment.contentType.includes(".document")) {
        return <BsFiletypeDocx size={20} />
      } else if (item.attachment.contentType.includes(".sheet")) {
        return <BsFiletypeXlsx size={20} />
      } else if (item.attachment.contentType.includes(".presentation")) {
        return <BsFiletypePptx size={20} />
      } else if (item.attachment.contentType.includes("csv")) {
        return <BsFiletypeCsv size={20} />
      }
    }
    return null
  }

  const isAttachmentValid =
    !item.attachment ||
    (!item.attachment.contentType.includes("csv") &&
      !item.attachment.contentType.includes(".presentation") &&
      !item.attachment.contentType.includes(".sheet") &&
      !item.attachment.contentType.includes(".document") &&
      !item.attachment.contentType.includes("pdf"))

  return (
    <div
      className={`message-doc ${isAttachmentValid && "invalid-doc-file"} ${loggedUser !== item.fromUserId ? "no-logged-user" : ""
        }`}
      onClick={handleAttachmentClick}
      title="Documento">
      {getFileIcon()}
      {isAttachmentValid ? (
        <span className="message-doc-name" title={item.message}>
          {item.message}
        </span>
      ) : (
        <span className="message-doc-name" title={item.attachment?.name}>
          {item.attachment?.name}
        </span>
      )}
    </div>
  )
}

const renderImage = (item) => <img src={item.attachment.remotePath} className="img-chat" />

const renderAudio = (item) => {
  // Função para detectar se o navegador é Safari
  const isIOSWebView = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera

    // Verificar se é um dispositivo iOS e não é o Safari
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent)

    // Se for iOS mas não for Safari, pode ser um WebView
    return isIOS && !isSafari
  }

  // Verifica se é Safari e se o URL contém 'https://api.twilio.com'
  const getAudioUrl = () => {
    if (isIOSWebView() && item.attachment.remotePath.includes("https://api.twilio.com")) {
      // Se for Safari e o URL for Twilio, usa o proxy
      return `https://be.manejechat.com/auth/audio?url=${item.attachment.remotePath}`
    } else {
      // Caso contrário, usa o URL original
      return item.attachment.remotePath
    }
  }

  return (
    <audio
      id="audio"
      preload="none"
      className="audio-component"
      src={getAudioUrl()} // Usa a URL condicional aqui
      controls
    />
  )
}

const renderVideo = (item) => (
  <video src={item.attachment.remotePath} width="320" height="240" controls id="video" />
)

const transformLinks = (message) => {
  const urlRegex = /(https?:\/\/[^\s/$.?#].[^\s]*)/gi

  return message.replace(urlRegex, (match) => {
    return `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`
  })
}

const textVerification = (valueSearch, item, loggedUser, chatId) => {
  if (
    item &&
    item.attachment &&
    item.attachment.contentType &&
    typeof item.attachment.contentType === "string"
  ) {
    if (item.attachment.contentType.includes("image")) {
      return renderImage(item)
    } else if (item.attachment.contentType.includes("audio")) {
      return renderAudio(item)
    } else if (item.attachment.contentType.includes("video")) {
      return renderVideo(item)
    } else {
      return <MessageDocComponent {...{ item, loggedUser }} />
    }
  } else {
    const cleanedText = cleanMessageTemplate(item?.content || item?.message)
    return (
      <p
        title={`${cleanedText}`}
        className={`message ${(loggedUser !== item.user && chatId === item.user) || item.user === "system_chat_id"
          ? "no-logged-user"
          : ""
          }`}>
        {getHighlightedText(cleanedText, valueSearch, chatId === item.user)}
      </p>
    )
  }
}

const getErrors = (error) => {
  let errorMessage = "Erro ao enviar mensagem. Tente novamente."

  switch (error) {
    case "63049":
      errorMessage =
        "A Meta escolheu não enviar sua mensagem. Mas não se preocupe, tente novamente em alguns minutos."
      break
    case "63016" || "63027":
      errorMessage =
        "O envio da mensagem falhou porque não está dentro dos parâmetros. Verifique e tente novamente."
      break
    case "63024":
      errorMessage =
        "O número de do whatsapp não é válido ou a versão do aplicativo é muito antiga."
      break
    case "63021":
      errorMessage = "O tipo de mensagem enviada não é permitida ou a mensagem é muito grande."
      break
    case "63018":
      errorMessage = "Limite de mensagens excedido, aguarde 24 horas para enviar novamente."
      break
    case "63012" || "63010":
      errorMessage = "Ocorreu um erro interno."
      break
    case "63024":
      errorMessage =
        "A mensagem não pode ser entregue porque o destinatário é um usuário inválido no WhatsApp ou outras restrições Meta para o WhatsApp."
      break
    case "63005":
      errorMessage = "Houve um problema com o conteúdo enviado ou o usuário pode não ser válido."
      break
    case "63030":
      errorMessage = "O template não aceita arquivos de mídia."
      break
    case "63003":
      errorMessage =
        "A mensagem não pode ser entregue porque o destinatário é um usuário inválido no WhatsApp."
      break
    default:
      break
  }

  return errorMessage
}

export default textVerification

export function isObjectEffectivelyEmpty(obj) {
  if (!obj || typeof obj !== 'object') return true;

  return Object.keys(obj).every(key => {
    const value = obj[key];

    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim().length === 0) ||
      (Array.isArray(value) && value.length === 0)
    );
  });
}